import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IUnifiedIntegration, AtsCandidate, AtsApplication, AtsJob } from './unified.types';

@Injectable({
  providedIn: 'root'
})
export class UnifiedService {

  private baseUrl = `${environment.backendurl}/unified/integrations`;

  constructor(private http: HttpClient) {}

  /**
   * Get all unified integrations
   * @returns {Observable<{ status: string, data: IUnifiedIntegration[], error?: string }>}
   */
  getUnifiedIntegrations(): Observable<{ status: string, data: IUnifiedIntegration[], error?: string }> {
    return this.http.get<{ status: string, data: IUnifiedIntegration[], error?: string }>(`${this.baseUrl}`);
  }

  /**
   * Get a specific unified integration by ID
   * @param {number} integrationId - The ID of the unified integration
   * @returns {Observable<{ status: string, data: IUnifiedIntegration, error?: string }>}
   */
  getUnifiedIntegrationById(integrationId: number): Observable<{ status: string, data: IUnifiedIntegration, error?: string }> {
    return this.http.get<{ status: string, data: IUnifiedIntegration, error?: string }>(`${this.baseUrl}/${integrationId}`);
  }

  /**
   * Get all unified integrations of the current user
   * @param {string} [q] - Optional query parameter to filter integrations by name
   * @param {boolean} [active] - Optional parameter to filter integrations by enabled status
   * @returns {Observable<{ status: string, data: (IUnifiedIntegration & { user: null | { enabled: boolean, connection_id: string }, can_connect: boolean })[], error?: string }>}
   */
  getUserUnifiedIntegrations(q?: string, active?: boolean): Observable<{ status: string, data: (IUnifiedIntegration & { user: null | { enabled: boolean, connection_id: string }, can_connect: boolean })[], error?: string }> {
    const params = { params: {} as any };
    if (q) {
      params.params.q = q;
    }
    if (active !== undefined) {
      params.params.active = active.toString();
    }
    return this.http.get<{ status: string, data: (IUnifiedIntegration & { user: null | { enabled: boolean, connection_id: string }, can_connect: boolean })[], error?: string }>(`${this.baseUrl}/me`, params);
  }

  /**
   * Enable a unified integration for the current user
   * @param {number} integrationId - The ID of the unified integration to enable
   * @param {string} connection_id - The connection ID for the integration
   * @returns {Observable<{ status: string, data: any, error?: string }>}
   */
  enableUnifiedIntegration(integrationId: number, connection_id: string): Observable<{ status: string, data: any, error?: string }> {
    return this.http.post<{ status: string, data: any, error?: string }>(`${this.baseUrl}/enable`, { unified_integration_id: integrationId, connection_id });
  }

  /**
   * Disable a unified integration for the current user
   * @param {number} integrationId - The ID of the unified integration to disable
   * @returns {Observable<{ status: string, data: any, error?: string }>}
   */
  disableUnifiedIntegration(integrationId: number): Observable<{ status: string, data: any, error?: string }> {
    return this.http.post<{ status: string, data: any, error?: string }>(`${this.baseUrl}/disable`, { unified_integration_id: integrationId });
  }

  /**
   * Get the redirect URL for a unified integration
   * @param {number} integrationId - The ID of the unified integration
   * @returns {Observable<{ status: string, data: any, error?: string }>}
   */
  getUnifiedIntegrationRedirectUrl(integrationId: number): Observable<{ status: string, data: string, error?: string }> {
    return this.http.get<{ status: string, data: any, error?: string }>(`${this.baseUrl}/${integrationId}/auth`);
  }

  /**
   * Create a candidate in the ATS via the unified API
   * @param {number} integrationId - The ID of the unified integration
   * @param {AtsCandidate} candidate - The candidate data to create
   * @returns {Observable<{ status: string, data: any, error?: string }>}
   */
  createCandidate(integrationId: number, candidate: AtsCandidate, opts: { force?: boolean, candidatenumber?: string } = { force: false }): Observable<{ status: string, data: any, error?: string }> {
    const requestBody: any = {
      data: candidate,
      force: !!opts.force
    };
    if (opts.candidatenumber) {
      requestBody.candidatenumber = opts.candidatenumber;
    }
    return this.http.post<{ status: string, data: any, error?: string }>(`${this.baseUrl}/${integrationId}/candidate`, requestBody);
  }

  /**
   * Query candidates in the ATS via the unified API
   * @param {number} integrationId - The ID of the unified integration
   * @param {string} query - The query string to search for candidates
   * @returns {Observable<{ status: string, data: AtsCandidate[], error?: string }>}
   */
  queryCandidates(integrationId: number, query: string): Observable<{ status: string, data: AtsCandidate[], error?: string }> {
    return this.http.get<{ status: string, data: AtsCandidate[], error?: string }>(`${this.baseUrl}/${integrationId}/candidates`, { params: { query } });
  }

  /**
   * Query jobs in the ATS via the unified API
   * @param {number} integrationId - The ID of the unified integration
   * @param {string} query - The query string to search for jobs
   * @returns {Observable<{ status: string, data: AtsJob[], error?: string }>}
   */
  queryJobs(integrationId: number, query: string): Observable<{ status: string, data: AtsJob[], error?: string }> {
    return this.http.get<{ status: string, data: AtsJob[], error?: string }>(`${this.baseUrl}/${integrationId}/job/query`, { params: { query } });
  }

  /**
   * Apply for a job in the ATS via the unified API
   * @param {number} integrationId - The ID of the unified integration
   * @param {string} jobId - The ID of the job to apply for
   * @param {AtsApplication} application - The application data
   * @param {Record<string, any>} [metadata] - Optional metadata to include with the application
   * @returns {Observable<{ status: string, data: any, error?: string }>}
   */
  createApplication(integrationId: number, jobId: string, application: AtsApplication, metadata?: Record<string, any>): Observable<{ status: string, data: AtsApplication, error?: string }> {
    return this.http.post<{ status: string, data: AtsApplication, error?: string }>(`${this.baseUrl}/${integrationId}/job/${jobId}/apply`, { candidate_id: application.candidate_id, auxilary_data: Object.entries(application).reduce((acc, [key, value]) => key !== 'candidate_id' ? { ...acc, [key]: value } : acc, {}), metadata: metadata || {} });
  }

  /**
   * Query applications in the ATS via the unified API
   * @param {number} integrationId - The ID of the unified integration
   * @param {string} jobId - The ID of the job
   * @param {string} [query] - Optional query string to search for applications
   * @param {string} [candidate] - Optional candidate ID to filter applications
   * @returns {Observable<{ status: string, data: AtsApplication[], error?: string }>}
   */
  queryApplications(integrationId: number, jobId: string, query?: string, candidate?: string): Observable<{ status: string, data: AtsApplication[], error?: string }> {
    const params: HttpParams = new HttpParams();
    if (query) {
      params.set('query', query);
    }
    if (candidate) {
      params.set('candidate', candidate);
    }
    return this.http.get<{ status: string, data: AtsApplication[], error?: string }>(`${this.baseUrl}/${integrationId}/job/${jobId}/application/query`, { params });
  }
}
